import { createRefPath, createRefPath_client } from "global/firestoreData/appData/appRefPaths";
import _ from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DatabaseContext } from "../DatabaseContext";
import { _showAppLogs } from "../LogContext";
import { PapsContext } from "../PapsContext";
import { _cacheGroupTypes } from "./useTimestamp";
import { useCachedData } from "./useCachedData";
import { gEnums } from "global/enums/globalEnums";
import { dataFix } from "global/common/dataFix";
import { writeBatch } from "firebase/firestore";
import { ParentContext } from "../ParentContext";
import { AppContext } from "../AppProvider";
import { getNow } from "global/common/dateFormatting";

const clientInfoCallTypes = {
  clients: 'clients',
  events: 'events',
}

export const useClientInfo = () => {

  const appContext = useContext(AppContext);
  const { app_state } = appContext ?? {}
  const { mainDb } = app_state ?? {}

  // databaseContext
  const databaseContext = useContext(DatabaseContext)
  const { database_fns } = databaseContext ?? {}

  const { paps_state } = useContext(PapsContext) ?? {};
  const { pathViews } = paps_state ?? {};

  const clientKey = pathViews?.clients;

  const [client, setClient] = useState();
  const [clientEvents, setClientEvents] = useState();

  useCachedData({
    cache_groupType: _cacheGroupTypes.clientInfo,
    cache_key: clientKey,
    fetchers: [
      { key: clientInfoCallTypes.events, getConfigFn: getConfig_clientEvents },
      { key: clientInfoCallTypes.clients, getConfigFn: getConfig_client },
    ],
    updateLocalState: (data) => {
      setClient(data?.[clientInfoCallTypes.clients] ?? {});
      setClientEvents(data?.[clientInfoCallTypes.events] ?? {});
    }
    // _.forEach(data, (value, key) => {
    //   switch (key) {
    //     case clientInfoCallTypes.clients:
    //       setClient(value);
    //       break;
    //     case clientInfoCallTypes.events:
    //       setClientEvents(value);
    //       break;
    //   }
    // });
    // }
  })

  const handleGet_eventsInfo = useCallback((callback) => {
    getEventsInfo(database_fns, pathViews, callback)
  }, []);

  const updateClientInfo = useCallback(async (currentEvent, callback) => {
    const _refPath = createRefPath_client(pathViews, ['events', currentEvent._itemKey])
    database_fns.set_doc(_refPath, currentEvent, true, callback)
  }, [])

  const updateClientInfos = useCallback(async (events, clientEventsStatus, callback) => {
    await update_clientInfos(mainDb, database_fns, pathViews, events, clientEventsStatus)
    callback()
  }, [])

  //   // 3️⃣ Stable memoized object
  const clientInfo = useMemo(() => ({ clients: client, events: clientEvents }), [client, clientEvents]);

  return { clientInfo, handleGet_eventsInfo, updateClientInfo, updateClientInfos };
};

// ------------------- Config Builders -------------------

const getConfig_client = (pathViews, callback) => ({
  refPath: createRefPath(["clients", pathViews.clients]),
  callback,
  opts: { listen: false },
});

const getConfig_clientEvents = (pathViews, callback) => ({
  refPath: createRefPath_client(pathViews, ["events"]),
  callback,
  opts: { listen: false },
});

const getEventsInfo = async (database_fns, pathViews, callback) => {

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);

  const _refPath = createRefPath_client(pathViews, ['events'])
  const events = await database_fns.get_data({ refPath: _refPath })

  const fetchEvents = async () => {
    // Calculate earliest and latest start dates for each event
    const updatedEvents = Object.keys(events).map(async (eventKey) => {

      const event = events[eventKey]
      const _refPath = createRefPath_client(pathViews, ['events', event._itemKey, 'sessions'])
      const sessions = await database_fns.get_data({ refPath: _refPath })

      if (Object.keys(sessions).length > 0) {

        const startDates = Object.keys(sessions).map((sessionKey) => new Date(sessions[sessionKey].startDate));
        const earliestStartDate = new Date(Math.min(...startDates));
        const latestStartDate = new Date(Math.max(...startDates));

        // Check if today's date is between earliestStartDate and latestStartDate
        const statuses = {
          isOngoing: today >= earliestStartDate && today <= latestStartDate,
          isCompleted: latestStartDate < new Date(),
          isRecent: latestStartDate >= lastMonth
        }

        return {
          ...event,
          startDate: formatDate(earliestStartDate),
          endDate: formatDate(latestStartDate),
          eventStatusType: statuses.isOngoing ? gEnums.eventStatusTypes.ongoing : statuses.isRecent ? gEnums.eventStatusTypes.recent : statuses.isCompleted ? gEnums.eventStatusTypes.completed : event.eventStatusType,
        };
      } else {
        return {
          ...event,
          eventStatusType: gEnums.eventStatusTypes.pending
        };
      }
    });

    // Update state with events including earliest and latest start dates
    Promise.all(updatedEvents).then((updatedEventsData) => {
      const latest = getSessionsData(updatedEventsData)
      const clientEventsStatus = _.orderBy(_.cloneDeep(updatedEventsData), ['startTimeUTC'], ['desc'])
      callback(updatedEventsData, latest, clientEventsStatus);
    })
  };

  fetchEvents();

}

const getSessionsData = (clientEvents, callback) => {

  // Find the next and last events based on earliestStartDate using lodash
  const sortedEvents = _.sortBy(clientEvents, (event) => new Date(event.earliestStartDate));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const nextEvent = _.find(sortedEvents, (event) => new Date(event.earliestStartDate) >= today);
  const lastEvent = _.last(sortedEvents);

  console.log('Next Event:', nextEvent);
  console.log('Last Event:', lastEvent);

  return { nextEvent, lastEvent }

}

const formatDate = (date) => {
  const pad = (num) => (num < 10 ? `0${num}` : num);
  const month = pad(date.getMonth() + 1); // Months are zero-based
  const day = pad(date.getDate());
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const update_clientInfos = async (mainDb, database_fns, pathViews, events, clientEvents) => {

  const allow = true

  const batch = writeBatch(mainDb);

  _.forEach(clientEvents, (itemData) => {
    const existingEvent = events?.[itemData._itemKey]
    if (existingEvent?.eventStatusType !== itemData?.eventStatusType) {
      const _refPath = createRefPath_client(pathViews, ['events', itemData._itemKey])
      const _selectedItem = _.cloneDeep(itemData)
      delete _selectedItem._itemKey
      delete _selectedItem.startTimeUTC
      delete _selectedItem.key
      delete _selectedItem.id
      delete _selectedItem._dateInfo
      const _cp = dataFix.removeAllEmpties(_selectedItem)
      database_fns.set_doc(_refPath, _cp, true, null, batch)
    }
  })

  if (allow) {
    const _timestamp = getNow()
    database_fns.update_timestamp(_cacheGroupTypes.clientInfo, pathViews.clients, _timestamp, batch)
    batch.commit();
  }

}